import { publicConfig } from "@/PublicConfig";
import { createCacheKey } from "@/utils/server/cache";
import { JobGetResponse, JobListResponse } from "@webmonitoring/vp-common";

interface CreateJobOptions {
  payload: {};
}

export const createJob = async (options: CreateJobOptions) => {
  const url = new URL(publicConfig.jobServiceEndpointV2URL);

  url.pathname = "/v2/jobs";

  const response = await fetch(url, {
    body: JSON.stringify(options.payload),
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw await response.json();
  }

  return await response.json();
};

interface GetJobOptions {
  authorization: string;
  changesListLength?: number;
  historyListLength?: number;
  jobId: string;
  workspaceId: string;
}

export const getJob = async (options: GetJobOptions): Promise<JobGetResponse | undefined> => {
  const url = new URL(publicConfig.jobServiceEndpointV2URL);

  url.pathname = `/v2/jobs/${options.jobId}`;

  url.searchParams.set("workspaceId", options.workspaceId);
  url.searchParams.set("jobId", options.jobId);

  if (options.changesListLength) {
    url.searchParams.set("changesListLength", String(options.changesListLength));
  }
  if (options.historyListLength) {
    url.searchParams.set("historyListLength", String(options.historyListLength));
  }

  const response = await fetch(url, {
    headers: {
      authorization: options.authorization,
    },
  });

  // A deleted job is a missing page, not a build error.
  if (response.status === 404) {
    return undefined;
  }

  if (!response.ok) {
    throw new Error();
  }

  const json: JobGetResponse = await response.json();

  // Strip some data that we don't want to take the risk Google bot will index
  json.changes = json.changes?.map((it) => ({
    ...it,
    ccache_snapshot: undefined,
  }));

  return json;
};

interface GetJobsOptions {
  authorization: string;
  cache?: boolean;
  pagination?: {
    index: string;
    size?: string;
  };
  workspaceId: string;
}

export const getJobs = async (options: GetJobsOptions): Promise<JobListResponse> => {
  const cacheKey = createCacheKey({
    content: [options?.pagination?.index, options?.pagination?.size, options.workspaceId],
    prefix: "get-jobs",
  });

  const cachedContent = globalThis.cache.get<JobListResponse>(cacheKey);

  if (options.cache && cachedContent) {
    return cachedContent;
  }

  const url = new URL(publicConfig.jobServiceEndpointV2URL);

  url.pathname = "/v2/jobs";

  url.searchParams.set("workspaceId", options.workspaceId);

  if (options.pagination) {
    url.searchParams.set("pageIndex", options.pagination.index);
    url.searchParams.set("pageSize", options.pagination.size ?? "100");
  }

  const response = await fetch(url, {
    headers: {
      authorization: options.authorization,
    },
  });

  if (!response.ok) {
    throw new Error();
  }

  const json = await response.json();

  if (options.cache) {
    const ttl = 43200;

    globalThis.cache.set(cacheKey, json, ttl);
  }

  return json;
};

interface GetAllJobsOptions {
  authorization: string;
  cache?: boolean;
  workspaceId: string;
}

interface GetAllJobsResult {
  items: JobListResponse["jobs"];
  response: JobListResponse[];
}

export const getAllJobs = async (options: GetAllJobsOptions): Promise<GetAllJobsResult> => {
  const jobs = await getJobs({
    authorization: options.authorization,
    cache: options.cache,
    workspaceId: options.workspaceId,
  });

  const tasks = Array(jobs.totalPages)
    .fill(1)
    .map((_, index) => {
      return getJobs({
        authorization: options.authorization,
        cache: options.cache,
        pagination: {
          index: String(index),
        },
        workspaceId: options.workspaceId,
      });
    });

  const responses = await Promise.all(tasks);

  return {
    items: responses.map((response) => response.jobs ?? []).flat(),
    response: responses,
  };
};
